const imageOptimizingOrigin: string = process.env.VSF_MAGENTO_IMAGE_OPTIMIZING_ORIGIN;
const alreadyOptimized: boolean = imageOptimizingOrigin === 'backend';

let mediaBackend: string = process.env.VSF_MAGENTO_BASE_URL + "media/";
if (!mediaBackend) {
    console.warn('A media backend URL should be defined in your config.');
    mediaBackend = 'https://backend.test/media/';
}

// Tests if a URL begins with `http:` or `https:` or `data:`
const absoluteUrl: RegExp = /^(data|http|https)?:/i;

// Simple path joiner that guarantees one and only one slash between segments
const joinUrls = (base: string, url: string): string =>
  (base.endsWith('/') ? base.slice(0, -1) : base) +
  '/' +
  (url.startsWith('/') ? url.slice(1) : url);

const mediaBases: Map<string, string> = new Map()
  .set('image-product', 'catalog/product/')
  .set('image-category', 'catalog/category/')
  .set('image-swatch', 'attribute/swatch/');

const getFileType = (url: URL): string => {
    const fileName = url.pathname.split('/').reverse()[0];
    const fileType = fileName.split('.').reverse()[0];
    return fileType;
};

/**
 * Creates an "optimized" url for a provided relative url based on
 * requested media type and width. Any image URLs (whose type begins with
 * "image-" will also be optimized.)
 *
 * If a `type` is provided the `path` will be joined with the associated media
 * base.
 *  - `catalog/product/path/to/img.jpg`
 *
 * If a `width` is provided, "resize parameters" are added to the URL for
 * middlewares (either onboard or backend) to return using the desired width
 * and original media url.
 *  - `catalog/product/path/to/img.jpg?width=500&auto=webp&format=pjpg
 *
 * If only `path` is provided it is returned unaltered.
 */
const makeOptimizedUrl = (path: string, { type, ...opts }: { type?: string, [key: string]: any } = {}): string => {
    // Immediate return if there's no image optimization to attempt
    if (!type || !type.startsWith('image-')) {
        return path;
    }

    const { origin } = globalThis.location || {};
    const isAbsolute: boolean = absoluteUrl.test(path);
    const magentoBackendURL: string = process.env.VSF_MAGENTO_BASE_URL;
    let baseURL: URL = new URL(path, mediaBackend);

    // If URL is relative and has a supported type, prepend media base onto path
    if (!isAbsolute && mediaBases.has(type)) {
        const mediaBase: string | undefined = mediaBases.get(type);
        if (mediaBase && !baseURL.pathname.includes(mediaBase)) {
            baseURL = new URL(joinUrls(mediaBase, path), mediaBackend);
        }
    }

    if (baseURL.href.startsWith(magentoBackendURL) && !alreadyOptimized) {
        // Replace URL base so optimization middleware can handle request
        baseURL = new URL(baseURL.href.slice(magentoBackendURL.length), origin);
    }

    // Append image optimization parameters
    const params: URLSearchParams = new URLSearchParams(baseURL.search);
    params.set('auto', 'webp'); // Use the webp format if available

    const imageFileType: string = getFileType(baseURL);
    if (imageFileType === 'png') {
        params.set('format', 'png'); // use png if webp is not available
    } else {
        params.set('format', 'pjpg'); // Use progressive JPG if webp is not available
    }

    Object.entries(opts).forEach(([key, value]) => {
        if (value !== undefined && value !== null) {
            params.set(key, value);
        }
    });
    baseURL.search = params.toString();
    if (baseURL.origin === origin) {
        return baseURL.href.slice(baseURL.origin.length);
    }
    return baseURL.href;
};

export default makeOptimizedUrl;